import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import MaintenanceGate from "@/components/MaintenanceGate";
import HomePage from "@/pages/HomePage";
import CollectionsPage from "@/pages/CollectionsPage";
import ReleaseDetailPage from "@/pages/ReleaseDetailPage";
import ContactPage from "@/pages/ContactPage";
import ArtistInfoPage from "@/pages/ArtistInfoPage";
import LegalPage from "@/pages/LegalPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentFailurePage from "@/pages/PaymentFailurePage";
import OrderRecoverPage from "@/pages/OrderRecoverPage";
import OrderReviewPage from "@/pages/OrderReviewPage";
import BlocklistPage from "@/pages/BlocklistPage";
import LiveVisitorsPage from "@/pages/LiveVisitorsPage";
import LegacyRedirect from "@/pages/LegacyRedirect";
import { useVisitorHeartbeat } from "@/hooks/useVisitorHeartbeat";
import AdminUploadPage from "@/pages/AdminUploadPage";
import AdminReleasesPage from "@/pages/AdminReleasesPage";
import AdminEditReleasePage from "@/pages/AdminEditReleasePage";
import AdminPagesPage from "@/pages/AdminPagesPage";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import AdminDiscountUrlsPage from "@/pages/AdminDiscountUrlsPage";
import AdminVisitorHistoryPage from "@/pages/AdminVisitorHistoryPage";
import LoginPage from "@/pages/LoginPage";
import ProtectedAdminRoute from "@/components/ProtectedAdminRoute";
import PromoAttacher from "@/components/PromoAttacher";

const pageVariants = {
  initial: { opacity: 0, y: 12 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -8 },
};
const pageTransition = { duration: 0.35, ease: [0.22, 1, 0.36, 1] };

const Page = ({ children }) => (
  <motion.div
    initial="initial"
    animate="in"
    exit="out"
    variants={pageVariants}
    transition={pageTransition}
    className="w-full"
  >
    {children}
  </motion.div>
);

const AnimatedRoutes = () => {
  const location = useLocation();
  useVisitorHeartbeat();
  return (
    <MaintenanceGate>
      <PromoAttacher />
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
        <Route path="/legacy/:slug" element={<Page><LegacyRedirect /></Page>} />
        <Route path="/products/:slug" element={<Page><LegacyRedirect /></Page>} />
        <Route path="/" element={<Page><HomePage /></Page>} />
        <Route path="/collections" element={<Page><CollectionsPage /></Page>} />
        <Route path="/release/:id" element={<Page><ReleaseDetailPage /></Page>} />
        <Route path="/contact" element={<Page><ContactPage /></Page>} />
        <Route path="/artist" element={<Page><ArtistInfoPage /></Page>} />
        <Route path="/legal/:slug" element={<Page><LegalPage /></Page>} />

        <Route path="/admin/login" element={<Page><LoginPage /></Page>} />
        <Route
          path="/admin/upload"
          element={
            <ProtectedAdminRoute>
              <Page><AdminUploadPage /></Page>
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/home"
          element={
            <ProtectedAdminRoute>
              <Page><AdminReleasesPage /></Page>
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/releases"
          element={
            <ProtectedAdminRoute>
              <Page><AdminReleasesPage /></Page>
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/releases/:id/edit"
          element={
            <ProtectedAdminRoute>
              <Page><AdminEditReleasePage /></Page>
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/pages"
          element={
            <ProtectedAdminRoute>
              <Page><AdminPagesPage /></Page>
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <ProtectedAdminRoute>
              <Page><AdminSettingsPage /></Page>
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedAdminRoute>
              <Page><AdminDashboardPage /></Page>
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/orders/review"
          element={
            <ProtectedAdminRoute>
              <Page><OrderReviewPage /></Page>
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/blocklist"
          element={
            <ProtectedAdminRoute>
              <Page><BlocklistPage /></Page>
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/live-visitors"
          element={
            <ProtectedAdminRoute>
              <Page><LiveVisitorsPage /></Page>
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/visitor-history"
          element={
            <ProtectedAdminRoute>
              <Page><AdminVisitorHistoryPage /></Page>
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/discount-urls"
          element={
            <ProtectedAdminRoute>
              <Page><AdminDiscountUrlsPage /></Page>
            </ProtectedAdminRoute>
          }
        />

        <Route path="/payment/success" element={<Page><PaymentSuccessPage /></Page>} />
        <Route path="/payment/failure" element={<Page><PaymentFailurePage /></Page>} />
        <Route path="/order/recover/:token" element={<Page><OrderRecoverPage /></Page>} />
        <Route path="/payment/cancel" element={<Page><HomePage /></Page>} />
      </Routes>
    </AnimatePresence>
    </MaintenanceGate>
  );
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <div className="App">
          <BrowserRouter>
            <AnimatedRoutes />
          </BrowserRouter>
          <Toaster position="bottom-right" theme="dark" />
        </div>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
